import React, { FC } from "react"
import styled from "@emotion/styled"
import { useTranslation } from "react-i18next"
import useSwipableContent from "@/utils/useSwipableContent"
import { mobileFirstDevice } from "@/utils/media-queries"
import { values } from "./data"
import { getWordBreak } from "../Text"

const OptionsContainer = styled.div`
  overflow: hidden;
  width: 100%;
  margin: 0 0 32px 0;
`

const Options = styled.div`
  display: flex;

  > * {
    flex: 0 0 auto;
  }
`

const Option = styled("div", { shouldForwardProp: prop => prop !== "light" })<{
  light: boolean
}>`
  border-bottom: ${({ light }) =>
    light ? "1px solid rgba(0,0,0,0.4)" : "1px solid rgba(244, 244, 244, 0.4)"};
  color: ${({ theme, light }) =>
    light ? theme.colors.black : theme.colors.titleText};
  padding-right: 20px;
`

const OptionText = styled("div", {
  shouldForwardProp: prop => prop !== "light" && prop !== "isSelected",
})<{ light: boolean; isSelected: boolean }>`
  white-space: nowrap;
  cursor: pointer;
  font-weight: 600;
  font-size: 18px;
  font-style: normal;
  line-height: 140%;
  margin-bottom: -1px;
  color: ${({ theme, light }) =>
    light ? theme.colors.black : theme.colors.titleText};
  opacity: ${({ isSelected }) => (isSelected ? 1 : 0.4)};
  padding-bottom: 8px;
  ${({ isSelected, light, theme }) =>
    isSelected
      ? `border-bottom: 1px solid ${
          light ? theme.colors.black : theme.colors.titleText
        }`
      : ""};
`

const EmptyOption = styled.div`
  width: 20%;
`

const DescriptionContainer = styled.div`
  overflow: hidden;
  width: 100%;
`

const Descriptions = styled.div`
  display: flex;
`

const DescriptionText = styled("div", {
  shouldForwardProp: prop => prop !== "light",
})<{ light: boolean }>`
  text-align: left;
  padding: 0;
  position: relative;
  min-width: 100%;
  display: block;
  width: 100%;
  font-weight: 600;
  font-size: 18px;
  font-style: normal;
  line-height: 140%;
  color: ${({ theme, light }) => (light ? theme.contentText : theme.tokenText)};
  white-space: pre-wrap;
  @media ${mobileFirstDevice.tablet} {
    font-size: 16px;
    line-height: 135%;
    letter-spacing: 0.015em;
  }
`

const Mobile: FC<{ light: boolean }> = ({ light }) => {
  const { menuRef, valuesRef, currentIndex, onTouch } = useSwipableContent()
  const { t, i18n } = useTranslation("values")

  return (
    <div>
      <OptionsContainer ref={menuRef}>
        <Options>
          {values.map((title, index) => (
            <Option key={title} light={light}>
              <OptionText
                key={title}
                light={light}
                onClick={() => onTouch(index)}
                isSelected={index === currentIndex}
                style={{ wordBreak: getWordBreak(i18n.language) }}
              >
                {t(`values.${index}_title`)}
              </OptionText>
            </Option>
          ))}
          <EmptyOption />
        </Options>
      </OptionsContainer>
      <DescriptionContainer ref={valuesRef}>
        <Descriptions>
          {values.map((title, index) => (
            <DescriptionText
              key={`${title}_description`}
              light={light}
              style={{ wordBreak: getWordBreak(i18n.language) }}
            >
              {t(`values.${index}_description`)}
            </DescriptionText>
          ))}
        </Descriptions>
      </DescriptionContainer>
    </div>
  )
}

export default Mobile
